// Tiny fetch wrapper. Sanctum SPA cookie auth — every request must include
// credentials. CSRF token comes via the XSRF-TOKEN cookie that Sanctum sets
// when we hit /sanctum/csrf-cookie before any state-changing request.

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let csrfPrimed = false;

async function primeCsrf(): Promise<void> {
  if (csrfPrimed) return;
  await fetch('/sanctum/csrf-cookie', { credentials: 'include' });
  csrfPrimed = true;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, payload: unknown) {
    super(`API ${status}`);
    this.status = status;
    this.payload = payload;
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
};

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  let url = `/api${path}`;
  if (opts.query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== '') params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  if (STATE_CHANGING.has(method)) {
    await primeCsrf();
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  }

  const resp = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  let payload: unknown = null;
  const text = await resp.text();
  if (text) {
    try { payload = JSON.parse(text); } catch { payload = text; }
  }

  if (!resp.ok) throw new ApiError(resp.status, payload);
  return payload as T;
}
